<template>
    <b-modal
        dialog-class="email-detail-modal"
        hide-footer
        :title="formContext.data.subject || ''"
        :visible="show"
        @hidden="$emit('hidden')">
        <b-row class="email-detail-modal__content">
            <b-col class="email-detail-modal__body-pane" cols="6">
                <div v-if="!formContext.isLoaded" class="d-flex justify-content-center pt-5">
                    <csp-loading-indicator/>
                </div>
                <template v-if="formContext.isLoaded">
                    <div
                        v-if="formContext.data.bodyHtml !== null && resolvedBodyHtml === null"
                        class="d-flex justify-content-center pt-5">
                        <csp-loading-indicator/>
                    </div>
                    <iframe
                        v-if="resolvedBodyHtml !== null"
                        class="email-detail-modal__iframe"
                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                        :srcdoc="resolvedBodyHtml"
                        width="100%"/>
                    <pre
                        v-else-if="formContext.data.bodyHtml === null"
                        class="email-detail-modal__plaintext">{{ formContext.data.bodyPlaintext }}</pre>
                </template>
            </b-col>
            <b-col class="email-detail-modal__form-pane" cols="6" style="position: relative;">
                <dl v-if="formContext.isLoaded" class="email-detail-modal__meta">
                    <div class="email-detail-modal__meta-item">
                        <dt>Betreff</dt>
                        <dd>{{ formContext.data.subject }}</dd>
                    </div>

                    <template
                        v-for="(recipientLabel, recipientType) in recipientTypeLabels">
                        <div
                            v-if="formContext.data.recipients && formContext.data.recipients[recipientType]"
                            :key="recipientType"
                            class="email-detail-modal__meta-item">
                            <dt>{{ recipientLabel }}</dt>
                            <dd>
                                <span
                                    v-for="(recipient, index) in formContext.data.recipients[recipientType]"
                                    :key="index">
                                    <!-- formatting required to avoid whitespace after text-with-tooltip component -->
                                    <csp-text-with-tooltip
                                        :text="recipient.name || recipient.emailAddress"
                                        :tooltip="recipient.emailAddress"/><template
                                        v-if="_notLastRecipient(index, recipientType)">, </template>
                                </span>
                            </dd>
                        </div>
                    </template>
                    <div class="row email-detail-modal__meta-item">
                        <div class="col-12 col-md-6 col-lg-4">
                            <dt>{{ formContext.data.mailType === 'sent' ? 'Gesendet' : 'Erhalten' }}</dt>
                            <dd>{{ formatDateTime(formContext.data.mailType === 'sent'
                                ? formContext.data.sentDateTime
                                : formContext.data.receivedOutlookDateTime) }}</dd>
                        </div>

                        <div class="col-12 col-md-6 col-lg-4">
                            <dt>Wichtigkeit</dt>
                            <dd>{{ importanceLabel }}</dd>
                        </div>

                        <div class="col-12 col-md-6 col-lg-4">
                            <dt>Anhänge</dt>
                            <dd>{{ formContext.data.hasAttachments ? formContext.data.attachmentCount : 'keine' }}</dd>
                        </div>
                    </div>
                </dl>

                <hr v-if="formContext.isLoaded"/>
                <!--
                    Editing status, reminderDate and assignedToUser inside the modal is temporarily
                    suspended (see ticket comment #46431). The InfoCard remains permanently in display
                    mode: formContext.isInEditMode is never set to true (handleEditModeByQuery=false,
                    no pencil icon since no link-route is provided), and show-buttons=false prevents
                    Save/Cancel buttons from appearing even if isInEditMode were accidentally set.
                    Re-enabling is a follow-up ticket.
                -->
                <csp-desktop-info-card
                    :cols="12"
                    :form-context="formContext"
                    icon="inticon-autoclaim_assign"
                    prevent-default-on-cancel
                    :show-buttons="false"
                    style="position: relative"
                    @cancel-form="_cancelEditMode"
                    @save="saveEmailDetail">
                    <div class="row">
                        <csp-desktop-info-card-element
                            class="col-12 col-md-6"
                            :form-context="formContext"
                            label="Status"
                            :options="statusOptions"
                            property="status"
                            :required="true"
                            type="autocomplete"/>

                        <csp-desktop-info-card-element
                            class="col-12 col-md-6"
                            :form-context="formContext"
                            label="Wiedervorlage"
                            :max-date="null"
                            :min-date="tomorrowString"
                            property="reminderDate"
                            type="datepicker"/>
                        <csp-desktop-info-card-element
                            class="col-12 col-md-6"
                            :form-context="formContext"
                            label="Bearbeiter/in"
                            :options="assignedUserOptions"
                            :options-loader="loadUserAssignmentOptions"
                            property="assignedToUserId"
                            type="autocomplete">
                            <template v-slot:display-value>
                                <span v-if="formContext.data.assignedToUserName"
                                      class="font-weight-bold">
                                    {{ formContext.data.assignedToUserName }}
                                </span>
                                <span v-else class="font-weight-bold text-muted">Kein Bearbeiter</span>
                            </template>
                        </csp-desktop-info-card-element>
                        <csp-desktop-info-card-element
                            class="col-12 col-md-6"
                            :form-context="formContext"
                            label="Kategorien"
                            property="categories"
                            type="text">
                            <template v-slot:display-value>
                                <span
                                    v-if="!formContext.data.categories || formContext.data.categories.length === 0"
                                    class="font-weight-bold text-muted">
                                    Keine Kategorien zugeordnet
                                </span>
                                <b-badge
                                    v-for="category in formContext.data.categories"
                                    :key="category.id"
                                    class="font-weight-bold mr-1"
                                    variant="secondary">
                                    {{ category.displayName }}
                                </b-badge>
                            </template>
                        </csp-desktop-info-card-element>
                    </div>
                </csp-desktop-info-card>
                <div v-if="formContext.isLoaded && formContext.isLoading === 0">
                    <p class="font-weight-bold mb-2">Zugeordnete Gutachten</p>
                    <div
                        v-if="!formContext.data.damageRecordAssignments
                            || formContext.data.damageRecordAssignments.length === 0"
                        class="text-muted mb-2">
                        Keine Gutachten zugeordnet
                    </div>
                    <div
                        v-for="(assignment, index) in formContext.data.damageRecordAssignments"
                        :key="index">
                        <a
                            :href="getAppraisalLink(assignment.vehicleDamageRecordId,
                                assignment.damageRecordAppraisalId)"
                            target="_blank">
                            {{ assignment.appraisalNumber }}
                        </a>
                    </div>
                    <b-button class="mt-2" size="sm" variant="primary" @click="openAssignModal">
                        Gutachtenzuordnung
                    </b-button>
                </div>
                <csp-email-damage-record-assign-modal
                    :email-id="emailId"
                    :show-modal="showAssignModal"
                    :subject="formContext.data.subject"
                    @assignments-changed="onAssignmentsChanged"
                    @update:showModal="showAssignModal = $event"/>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script>
import '@desktop/scss/components/digital-mailbox.scss';
import Container from '@services/Container';
import LoadingIndicator from '@components/LoadingIndicator';
import EmailDamageRecordAssignModal from './EmailDamageRecordAssignModal.vue';
import TextWithTooltip from '@desktop/components/TextWithTooltip.vue';
import {formatDateTime} from '@js/functions/date';

const container = Container.getInstance();
const digitalMailboxRepository = container.getDigitalMailboxRepository();

export default {
    name: 'EmailDetailModal',
    components: {
        'csp-email-damage-record-assign-modal': EmailDamageRecordAssignModal,
        'csp-loading-indicator': LoadingIndicator,
        'csp-text-with-tooltip': TextWithTooltip,
    },
    provide() {
        return {
            // define empty function to satisfy injection in InfoCard
            emitFieldChange: () => {},
        };
    },
    props: {
        formContext: {
            required: true,
            type: Object,
        },
        show: {
            required: true,
            type: Boolean,
        },
    },
    emits: ['email-updated', 'hidden'],
    data() {
        return {
            resolvedBodyHtml: null,
            showAssignModal: false,
            userAssignmentOptions: [],
        };
    },
    computed: {
        assignedUserOptions() {
            if (this.userAssignmentOptions.length > 0) {
                return this.userAssignmentOptions;
            }
            // Before options load: synthesize current assignment so the field isn't blank.
            const userId = this.formContext.data.assignedToUserId;
            const userName = this.formContext.data.assignedToUserName;
            if (userId && userName) {
                return [{value: userId, text: userName}];
            }
            return [];
        },
        importanceLabel() {
            const labels = {
                high: 'Hoch',
                low: 'Niedrig',
                normal: 'Normal',
            };
            return labels[this.formContext.data.importance] || '—';
        },
        emailId() {
            return this.formContext.objectId ?? null;
        },
        recipientTypeLabels() {
            return {
                from: 'Von',
                to: 'An',
                cc: 'Cc',
                bcc: 'Bcc',
                replyTo: 'Antwort an',
            };
        },
        statusOptions() {
            return [
                {value: 'open', text: 'Offen'},
                {value: 'reminder', text: 'Wiedervorlage', disabled: true},
                {value: 'done', text: 'Erledigt'},
            ];
        },
        tomorrowString() {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow.toISOString().slice(0, 10);
        },
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this._loadDetail();
            }
            else {
                this._reset();
            }
        },
    },
    methods: {
        formatDateTime(value) {
            return formatDateTime(value);
        },
        getAppraisalLink(vehicleDamageRecordId, damageRecordAppraisalId) {
            return '/de/Schaden-Manager/Akte/Detail/vehicleDamageRecordId/'
                + vehicleDamageRecordId
                + '/cost-units/appraisals/'
                + damageRecordAppraisalId;
        },
        loadUserAssignmentOptions() {
            return digitalMailboxRepository.loadUserAssignmentOptions(this.emailId);
        },
        _notLastRecipient(index, recipientType) {
            return index < this.formContext.data.recipients[recipientType].length - 1;
        },
        onAssignmentsChanged() {
            this.$emit('email-updated');
            this._loadDetail();
        },
        openAssignModal() {
            this.showAssignModal = true;
        },
        saveEmailDetail() {
            const ctx = this.formContext;
            ctx.form.clearValidationErrors();
            ctx.isLoading++;
            digitalMailboxRepository.updateEmail(ctx.objectId, ctx.form.getData())
                .then((response) => {
                    ctx.isInEditMode = false;
                    const attrs = response.data?.attributes ?? {};
                    this.$set(ctx, 'data', {...ctx.data, ...attrs});
                    ctx.form.setData({...ctx.data});
                    this.$emit('email-updated');
                })
                .catch((err) => {
                    ctx.form.setValidationErrors(err);
                })
                .finally(() => {
                    ctx.isLoading--;
                });
        },
        _loadDetail() {
            const ctx = this.formContext;
            if (!ctx.objectId) {
                return;
            }
            ctx.isLoading++;
            digitalMailboxRepository.loadEmailDetail(ctx.objectId)
                .then((response) => {
                    const resource = response.data?.data ?? response.data;
                    const attrs = resource?.attributes ?? {};
                    const fields = attrs.fields ?? {};
                    // Flatten field values (same as FormDataMixin.flattenAttributes)
                    const flattened = Object.fromEntries(
                        Object.entries(fields).map(([k, v]) => [k, v?.value ?? null])
                    );
                    this.$set(ctx, 'data', flattened);
                    this.$set(ctx, 'permissions', resource?.meta?.permissions ?? {});
                    ctx.form.setData({...flattened});
                    ctx.isLoaded = true;

                    // Opening an unread mail marks it as read server-side. Notify the parent so it
                    // can update the table row when the modal closes.
                    if (!ctx.hasBeenRead) {
                        this.$emit('email-updated');
                    }

                    if (flattened.bodyHtml !== null) {
                        digitalMailboxRepository.resolveInlineAttachmentImages(flattened.bodyHtml)
                            .then((resolved) => {
                                this.resolvedBodyHtml = resolved;
                            });
                    }
                })
                .finally(() => {
                    ctx.isLoading--;
                });
        },
        _reset() {
            const ctx = this.formContext;
            ctx.isLoaded = false;
            ctx.isInEditMode = false;
            ctx.data = {};
            ctx.form.reset();
            this.resolvedBodyHtml = null;
            this.userAssignmentOptions = [];
            // Invalidate the lazy-loaded user assignment options so that the next open
            // re-fetches them for the correct email (email ID may have changed).
            this.$root.$emit('lazy-select-reset', 'assignedToUserId');
        },
        _cancelEditMode() {
            this.formContext.isInEditMode = false;
            this.formContext.form.reset();
        },
    },
};
</script>
